import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useCallback, useMemo } from 'react';
import {
  Stack,
  Typography,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Paper,
  Box,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient, useQueries } from 'react-query';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EuroIcon from '@mui/icons-material/Euro';

import { ReadOnlyDataGrid, ReadOnlyField, RowAction } from '../../ReadOnlyDataGrid';
import {
  DiagnosisInterventionsReadDTO,
  DiagnosisInterventionsCreateDTO,
  DiagnosisReadDTO,
  DiagnosisEnergyVectorsReadDTO,
  DiagnosisEnergyUseReadDTO,
  IDiagnosisInterventionsCreateDTO,
} from '../../../generated/api-client';
import { apiClient, unitCostOverrideApi } from '../../../services/apiClient';
import { DiagnosisStepHandle } from './Step.types';
import {
  useDiagnosisEnergyVectorsByDiagnosisId,
  useDiagnosisEnergyUseByDiagnosisId,
} from '../../../hooks';
import { EditInterventionDialog } from './EditInterventionDialog';
import { UnitCostOverrideDialog } from './UnitCostOverrideDialog';
import { GridRowId } from '@mui/x-data-grid';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ColumnMappingDialog, type ColumnMappingField } from '../../common/ColumnMappingDialog';
import { getEnergyVectorOptionLabel } from '../../common/EnergyVectorDropdown';
import { ActionSpacer } from '../../common';
import { AiSearchModal } from '../../search/AiSearchModal';
import { fetchAiRecommender, type AiSearchResultItem } from '../../../services/aiSearchService';
import { aiFonteFromItem, aiFonteReadOnlyField } from '../../../utils/aiFonteDisplay';
import {
  importAiItemToDiagnosis,
  resolveAiInterventionRow,
} from '../../../utils/aiDiagnosisImport';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { formatNumberItalian } from '../../../utils/numberFormat';
import {
  parseTSVWithQuotedFields,
  normalizeInterventionsPasteRow,
} from '../../../utils/parseTsvWithQuotedFields';
import {
  buildEnergyUseIdMap,
  buildInterventionCloneCreateDto,
  buildInterventionGridRows,
  buildVectorIdMap,
  type InterventionGridRow,
} from '../../../utils/interventionCrossDiagnosisClone';

/**
 * Step Interventi: oltre alla griglia storica sugli interventi della diagnosi corrente, offre
 * viste alternative (tab) e import da altra diagnosi / da suggerimenti AI.
 *
 * - **Attuali**: interventi salvati su questa diagnosi; azioni modifica / ordine / elimina.
 * - **Diagnosi precedente**: stessa struttura colonne; dati dalla diagnosi immediatamente precedente
 *   sullo stesso *sito base* (GET `/diagnosis/{id}/previous_for_same_base_site` → `null` se assente).
 * - **Suggeriti AI**: stessa griglia; dati da API dedicata (TODO: ancora non cablata → lista vuota).
 * - **Riepilogo**: tre tabelle compatte (ID, titolo, vettori) per confrontare le tre fonti.
 *
 * Colonna **←** (solo precedente / AI / riepilogo con import): crea un nuovo intervento sulla diagnosi
 * attuale rimappando vettori e usi energetici (`interventionCrossDiagnosisClone`).
 */
type InterventionsMainView = 'current' | 'previous' | 'ai' | 'summary';

interface DiagnosisInterventionsStepProps {
  diagnosisId?: string;
}

export const DiagnosisInterventionsStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisInterventionsStepProps>(({ diagnosisId }, ref) => {
    const queryClient = useQueryClient();
    const { data: vectorTypeOptions = [] } = useVectorTypeOptions();
    const [mainView, setMainView] = useState<InterventionsMainView>('current');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedIntervention, setSelectedIntervention] =
      useState<DiagnosisInterventionsReadDTO | null>(null);
    const [deleteDialogState, setDeleteDialogState] = useState<{
      open: boolean;
      id: string | null;
    }>({ open: false, id: null });
    const [pasteMappingDialog, setPasteMappingDialog] = useState<{
      open: boolean;
      clipboardData: string[][];
    }>({ open: false, clipboardData: [] });
    const [unitCostOverrideDialogOpen, setUnitCostOverrideDialogOpen] = useState(false);
    const [aiSearchOpen, setAiSearchOpen] = useState(false);

    const { data: interventions = [], isLoading } = useQuery<DiagnosisInterventionsReadDTO[]>(
      `diagnosis-interventions-${diagnosisId}`,
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_interventions_search_by_diagnosis_id__diagnosis_id__get(
          diagnosisId!
        ),
      { enabled: !!diagnosisId }
    );

    /** Diagnosi cronologicamente precedente sullo stesso sito base (`customer_sites` via `original_site_id`). */
    const { data: previousDiagnosis, isLoading: isLoadingPreviousDiagnosis } =
      useQuery<DiagnosisReadDTO | null>(
        ['diagnosis-previous-for-same-base-site', diagnosisId],
        () =>
          apiClient.get_previous_diagnosis_for_same_base_site_diagnosis__diagnosis_id__previous_for_same_base_site_get(
            diagnosisId!
          ),
        { enabled: !!diagnosisId }
      );

    const previousDiagnosisId = previousDiagnosis?.id;

    /** Interventi della diagnosi precedente (se esiste); altrimenti query disabilitata. */
    const { data: previousInterventions = [], isLoading: isLoadingPrevious } = useQuery<
      DiagnosisInterventionsReadDTO[]
    >(
      `diagnosis-interventions-${previousDiagnosisId}`,
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_interventions_search_by_diagnosis_id__diagnosis_id__get(
          previousDiagnosisId!
        ),
      { enabled: !!previousDiagnosisId }
    );

    const { data: previousVectors = [] } = useDiagnosisEnergyVectorsByDiagnosisId(
      previousDiagnosisId || ''
    );
    const { data: previousUses = [] } = useDiagnosisEnergyUseByDiagnosisId(previousDiagnosisId);

    type AiInterventionBundle = {
      item: AiSearchResultItem;
      row: DiagnosisInterventionsReadDTO;
    };

    const { data: aiInterventionBundles = [], isLoading: isLoadingAiInterventions } = useQuery<
      AiInterventionBundle[]
    >(
      [
        'ai-recommender-interventions',
        diagnosisId,
        interventions
          .map(i => (i.code || '').trim())
          .filter(Boolean)
          .join('|'),
      ],
      async () => {
        const items = await fetchAiRecommender(diagnosisId!, 'intervention');
        const existingCodes = new Set(
          interventions.map(i => (i.code || '').trim()).filter(Boolean)
        );
        const filtered = items.filter(it => {
          const c = (String(it.payload.code ?? it.payload.title ?? '').trim() || '').trim();
          return !c || !existingCodes.has(c);
        });
        return Promise.all(
          filtered.map(async item => ({
            item,
            row: await resolveAiInterventionRow(item, apiClient),
          }))
        );
      },
      { enabled: !!diagnosisId, keepPreviousData: true }
    );

    const aiInterventions = useMemo(
      () => aiInterventionBundles.map(b => b.row),
      [aiInterventionBundles]
    );

    const aiItemByRowId = useMemo(() => {
      const m = new Map<string, AiSearchResultItem>();
      aiInterventionBundles.forEach(b => {
        if (b.row.id) m.set(b.row.id, b.item);
      });
      return m;
    }, [aiInterventionBundles]);

    const aiSourceDxIds = useMemo(
      () =>
        Array.from(
          new Set(
            aiInterventions
              .map(i => i.diagnosis_id)
              .filter((x): x is string => typeof x === 'string' && !!x && x !== diagnosisId)
          )
        ),
      [aiInterventions, diagnosisId]
    );

    const aiVectorQueries = useQueries(
      aiSourceDxIds.map(did => ({
        queryKey: [`diagnosis-energy-vectors-${did}`],
        queryFn: () =>
          apiClient.search_by_diagnosis_id_diagnosis_energy_vectors_search_by_diagnosis_id__diagnosis_id__get(
            did
          ),
        enabled: !!did && aiSourceDxIds.length > 0,
      }))
    );

    const aiUseQueries = useQueries(
      aiSourceDxIds.map(did => ({
        queryKey: [`diagnosis-energy-use-${did}`],
        queryFn: () =>
          apiClient.search_by_diagnosis_id_diagnosis_energy_use_search_by_diagnosis_id__diagnosis_id__get(
            did
          ),
        enabled: !!did && aiSourceDxIds.length > 0,
      }))
    );

    const aiVectorsByDx = useMemo(() => {
      const out: Record<string, DiagnosisEnergyVectorsReadDTO[]> = {};
      aiSourceDxIds.forEach((id, i) => {
        out[id] = (aiVectorQueries[i]?.data as DiagnosisEnergyVectorsReadDTO[]) ?? [];
      });
      return out;
    }, [aiSourceDxIds, aiVectorQueries]);

    const aiUsesByDx = useMemo(() => {
      const out: Record<string, DiagnosisEnergyUseReadDTO[]> = {};
      aiSourceDxIds.forEach((id, i) => {
        out[id] = (aiUseQueries[i]?.data as DiagnosisEnergyUseReadDTO[]) ?? [];
      });
      return out;
    }, [aiSourceDxIds, aiUseQueries]);

    const { data: unitCostOverrides = [] } = useQuery(
      `unit-cost-override-${diagnosisId}`,
      () => unitCostOverrideApi.listByDiagnosis(diagnosisId!),
      { enabled: !!diagnosisId }
    );

    const { data: energyVectors = [] } = useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId || '');

    // Carica gli usi energetici della diagnosi
    const { data: energyUses = [] } = useDiagnosisEnergyUseByDiagnosisId(diagnosisId);

    const deleteMutation = useMutation(
      async (id: string) => {
        await apiClient.delete_item_diagnosis_interventions__item_id__delete(id);
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
          toast.success('Intervento eliminato con successo');
          setDeleteDialogState({ open: false, id: null });
        },
        onError: (error: any) => {
          toast.error(error?.message || "Eliminazione dell'intervento non riuscita");
        },
      }
    );

    const moveUpMutation = useMutation(
      async (id: string) => {
        return apiClient.move_up_diagnosis_interventions__item_id__move_up_post(id);
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
          toast.success('Ordine aggiornato');
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Impossibile spostare in alto');
        },
      }
    );

    const moveDownMutation = useMutation(
      async (id: string) => {
        return apiClient.move_down_diagnosis_interventions__item_id__move_down_post(id);
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
          toast.success('Ordine aggiornato');
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Impossibile spostare in basso');
        },
      }
    );

    React.useImperativeHandle(ref, () => ({
      save: async () => ({ success: true }),
      add: async () => {
        if (!diagnosisId) {
          toast.error('Salva prima i dati principali della diagnosi.');
          return;
        }
        setSelectedIntervention(null);
        setEditDialogOpen(true);
      },
    }));

    // Funzione per formattare la priorità
    const formatPriority = (value: string | null | undefined): string => {
      if (!value) return '-';
      return value.charAt(0).toUpperCase() + value.slice(1);
    };

    // Funzione per formattare il tipo
    const formatType = (value: string | null | undefined): string => {
      if (!value) return '-';
      return value.charAt(0).toUpperCase() + value.slice(1);
    };

    // Funzione per formattare l'uso energetico
    const formatEnergyUse = (value: string | null | undefined): string => {
      if (!value) return '-';
      const energyUse = energyUses.find(eu => eu.id === value);
      return energyUse?.code || '-';
    };

    const handleEditClick = (id: GridRowId) => {
      const intervention = interventions.find(i => i.id === id);
      if (intervention) {
        setSelectedIntervention(intervention);
        setEditDialogOpen(true);
      }
    };

    const handleDeleteClick = (id: GridRowId) => {
      setDeleteDialogState({ open: true, id: id as string });
    };

    const handleCloseDeleteDialog = () => {
      setDeleteDialogState({ open: false, id: null });
    };

    const handleConfirmDelete = () => {
      if (deleteDialogState.id) {
        deleteMutation.mutate(deleteDialogState.id);
      }
    };

    const handleCloseEditDialog = () => {
      setEditDialogOpen(false);
      setSelectedIntervention(null);
    };

    // Per ogni vettore: una colonna consumo ante; poi una sola colonna costo ante totale (€)
    const consumptionColumnsByVector: ReadOnlyField[] = energyVectors.map(v => {
      const labelVectorUni = getEnergyVectorOptionLabel(v, vectorTypeOptions);
      return {
        field: `consumption_before_${v.id}`,
        headerName: `Consumo ante intervento ${labelVectorUni}`,
        width: 260,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_consumo_ante,
      };
    });
    const costBeforeTotalColumn: ReadOnlyField = {
      field: 'cost_before',
      headerName: 'Costo ante intervento [€/a]',
      width: 200,
      type: 'number',
      decimalPlaces: DECIMAL_PLACES.interventi_costo_ante,
    };

    // Una colonna "Risparmio energetico atteso [VE] (udm)" per vettore; se to_evaluated mostra "Da valutare"
    const savingColumnsByVector: ReadOnlyField[] = energyVectors.map(v => {
      const labelVectorUni = getEnergyVectorOptionLabel(v, vectorTypeOptions);
      const vectorId = v.id!;
      return {
        field: `annual_energy_saving_${vectorId}`,
        headerName: `Risparmio energetico annuo atteso ${labelVectorUni}`,
        width: 260,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_risparmio_energetico,
        renderCell: (params: any) => {
          const row = params?.row ?? {};
          const toEval = row.to_evaluated_by_vector?.[vectorId];
          if (toEval) return 'Da valutare';
          const val = row[`annual_energy_saving_${vectorId}`];
          return val != null ? formatNumberItalian(val) : '-';
        },
      };
    });

    /** Layout export Excel (una colonna consumo); la griglia ha N colonne consumo → non usare `fields` nel dialog incolla. */
    const pasteMappingFields = React.useMemo((): ColumnMappingField[] => {
      const savingForPaste: ColumnMappingField[] = energyVectors.map(v => ({
        field: `annual_energy_saving_${v.id}`,
        headerName: `Risparmio energetico annuo atteso ${getEnergyVectorOptionLabel(v, vectorTypeOptions)}`,
        width: 260,
      }));
      return [
        { field: 'order', headerName: '#', width: 50 },
        { field: 'code', headerName: 'ID intervento', width: 150 },
        { field: 'title', headerName: 'Titolo intervento', width: 200 },
        { field: 'energy_vectors', headerName: 'Vettori energetici', width: 220 },
        {
          field: 'consumption_import_single',
          headerName: 'Consumo ante intervento [udm] (colonna singola)',
          width: 260,
          calculated: true,
        },
        {
          field: 'cost_before',
          headerName: 'Costo ante intervento [€/a]',
          width: 200,
          calculated: true,
        },
        { field: 'energy_uses', headerName: 'Uso Energetico', width: 220 },
        { field: 'description', headerName: 'Descrizione', width: 250 },
        { field: 'type', headerName: 'Tipologia intervento', width: 150 },
        { field: 'incentives', headerName: 'Incentivi ottenibili', width: 150 },
        { field: 'priority', headerName: 'Priorità', width: 120 },
        { field: 'comments', headerName: 'Commenti e valutazioni interne', width: 250 },
        { field: 'investment', headerName: 'Investimento previsto [€]', width: 180 },
        ...savingForPaste,
        {
          field: 'annual_co2_saving',
          headerName: 'Risparmio CO2 annuo atteso [t]',
          width: 220,
          calculated: true,
        },
        {
          field: 'expected_economic_saving',
          headerName: 'Risparmio economico annuo atteso [€/a]',
          width: 280,
        },
        { field: 'useful_life', headerName: 'Vita utile intervento [a]', width: 180 },
        { field: 'payback_time', headerName: 'Tempo di pay-back [a]', width: 160 },
        { field: 'van_5_10_years', headerName: 'VAN 5% 10 anni [€]', width: 140 },
        { field: 'tir_10_years', headerName: 'TIR 10 anni [%]', width: 120 },
        { field: 'is_to_export', headerName: 'Esporta report', width: 120 },
      ];
    }, [energyVectors, vectorTypeOptions]);

    // Ordine colonne: #, ID intervento, Titolo, Vettori, Consumo ante, Costo ante [€/a], Uso Energetico, Descrizione, Tipologia, Incentivi, Priorità, Commenti, Investimento, Risparmio energetico annuo atteso (per vettore), Risparmio CO2, Risparmio economico, Vita utile, Pay-back, VAN, TIR
    const fields: ReadOnlyField[] = [
      { field: 'order', headerName: '#', width: 50, type: 'number' },
      { field: 'code', headerName: 'ID intervento', width: 150, sticky: 'left' },
      { field: 'title', headerName: 'Titolo intervento', width: 200, sticky: 'left' },
      {
        field: 'energy_vectors',
        headerName: 'Vettori energetici',
        width: 220,
        valueFormatter: (value: string[] | null | undefined) => {
          if (!value || !Array.isArray(value) || value.length === 0) return '-';
          return (
            value
              .map(id => {
                const vector = energyVectors.find(v => v.id === id);
                return vector ? getEnergyVectorOptionLabel(vector, vectorTypeOptions, false) : null;
              })
              .filter(Boolean)
              .join(', ') || '-'
          );
        },
      },
      ...consumptionColumnsByVector,
      costBeforeTotalColumn,
      {
        field: 'energy_uses',
        headerName: 'Uso Energetico',
        width: 220,
        valueFormatter: (value: string[] | null | undefined) => {
          if (!value || !Array.isArray(value) || value.length === 0) return '-';
          return (
            value
              .map(id => formatEnergyUse(id))
              .filter(Boolean)
              .join(', ') || '-'
          );
        },
        renderCell: (params: any) => {
          const row = params?.row ?? {};
          if (row.all_energy_use) return 'Tutti gli usi energetici';
          const value = params?.value ?? row.energy_uses;
          if (!value || !Array.isArray(value) || value.length === 0) return '-';
          return (
            value
              .map((id: string) => formatEnergyUse(id))
              .filter(Boolean)
              .join(', ') || '-'
          );
        },
      },
      { field: 'description', headerName: 'Descrizione', width: 250 },
      {
        field: 'type',
        headerName: 'Tipologia intervento',
        width: 150,
        valueFormatter: value => formatType(value),
      },
      { field: 'incentives', headerName: 'Incentivi ottenibili', width: 150 },
      {
        field: 'priority',
        headerName: 'Priorità',
        width: 120,
        valueFormatter: value => formatPriority(value),
      },
      { field: 'comments', headerName: 'Commenti e valutazioni interne', width: 250 },
      {
        field: 'investment',
        headerName: 'Investimento previsto [€]',
        width: 180,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_investimento,
        renderCell: (params: any) => {
          const row = params?.row ?? {};
          if (row.investment_to_evaluated) return 'Da valutare';
          const val = row.investment;
          return val != null ? formatNumberItalian(val) : '-';
        },
      },
      ...savingColumnsByVector,
      {
        field: 'annual_co2_saving',
        headerName: 'Risparmio CO2 annuo atteso [t]',
        width: 220,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_risparmio_co2,
      },
      {
        field: 'expected_economic_saving',
        headerName: 'Risparmio economico annuo atteso [€/a]',
        width: 280,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_risparmio_economico,
      },
      {
        field: 'useful_life',
        headerName: 'Vita utile intervento [a]',
        width: 180,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_vita_utile,
      },
      {
        field: 'payback_time',
        headerName: 'Tempo di pay-back [a]',
        width: 160,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_payback,
      },
      {
        field: 'van_5_10_years',
        headerName: 'VAN 5% 10 anni [€]',
        width: 140,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_van,
      },
      {
        field: 'tir_10_years',
        headerName: 'TIR 10 anni [%]',
        width: 120,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.interventi_tir,
      },
      {
        field: 'is_to_export',
        headerName: 'Esporta report',
        width: 120,
        renderCell: (params: any) => (
          <Checkbox
            size="small"
            checked={!!params?.row?.is_to_export}
            disabled
            sx={{ p: 0 }}
            inputProps={{ 'aria-label': 'Esporta nel report finale' }}
          />
        ),
      },
    ];

    // Funzione per aprire il dialog di mapping
    const handlePaste = useCallback(async () => {
      if (!diagnosisId) {
        toast.error('Salva prima i dati principali della diagnosi.');
        return;
      }

      try {
        const text = await navigator.clipboard.readText();
        // Parser TSV che rispetta campi quotati: newline/tab dentro "..." non spezzano righe/colonne
        const parsed = parseTSVWithQuotedFields(text);
        let clipboardData: string[][] = parsed
          .map(row => row.map(col => col.trim()))
          .map(normalizeInterventionsPasteRow);
        if (
          clipboardData.length > 0 &&
          clipboardData[clipboardData.length - 1].every(c => c === '')
        ) {
          clipboardData = clipboardData.slice(0, -1);
        }

        if (clipboardData.length === 0) {
          toast.error('Nessun dato trovato nel clipboard');
          return;
        }

        // Apri il dialog con i dati
        setPasteMappingDialog({
          open: true,
          clipboardData,
        });
      } catch (error) {
        console.error('Errore durante il paste:', error);
        toast.error('Errore durante la lettura del clipboard');
      }
    }, [diagnosisId]);

    // Funzione per importare i dati con il mapping personalizzato
    const handleImportWithMapping = useCallback(
      async (mapping: Map<number, string>) => {
        if (!diagnosisId) {
          toast.error('Salva prima i dati principali della diagnosi.');
          return;
        }

        if (mapping.size === 0) {
          toast.error('Mappa almeno una colonna a un campo');
          return;
        }

        const newItems: DiagnosisInterventionsCreateDTO[] = [];

        // Helper per parsare numeri
        const parseNumber = (value: string): number | undefined => {
          const cleaned = value.replace(/[^\d.,-]/g, '').replace(',', '.');
          const num = parseFloat(cleaned);
          return isNaN(num) ? undefined : num;
        };

        for (const row of pasteMappingDialog.clipboardData) {
          if (row.length === 0) continue;

          const itemData: any = {
            diagnosis_id: diagnosisId,
          };

          // Usa il mapping personalizzato per mappare le colonne ai campi
          mapping.forEach((fieldName, columnIndex) => {
            if (row[columnIndex] === undefined || row[columnIndex] === '') return;

            const value = row[columnIndex].trim();
            if (!value) return;

            // Gestisci energy_vectors: valori multipli separati da virgola o punto e virgola, risolti a id (anche per label come in tabella/dropdown)
            if (fieldName === 'energy_vectors') {
              const parts = value
                .split(/[,;]/)
                .map(s => s.trim())
                .filter(Boolean);
              const ids: string[] = [];
              for (const part of parts) {
                let vector = energyVectors.find(
                  v =>
                    (v as any)?.code === part ||
                    v.table_code_index === part ||
                    v.vector_type === part ||
                    v.id === part
                );
                if (!vector) {
                  vector = energyVectors.find(
                    v =>
                      getEnergyVectorOptionLabel(v, vectorTypeOptions, false) === part ||
                      getEnergyVectorOptionLabel(v, vectorTypeOptions, true) === part
                  );
                }
                if (vector?.id) ids.push(vector.id);
              }
              if (ids.length) itemData.energy_vectors = ids;
            }
            // Gestisci energy_uses: multipli separati da virgola o punto e virgola; "tutti gli usi" imposta all_energy_use
            else if (fieldName === 'energy_uses') {
              const v = value.toLowerCase().trim();
              if (
                v === 'tutti' ||
                v === 'tutti gli usi' ||
                v === 'tutti gli usi energetici' ||
                v === 'all'
              ) {
                itemData.all_energy_use = true;
                itemData.energy_uses = [];
              } else {
                const parts = value
                  .split(/[,;]/)
                  .map(s => s.trim())
                  .filter(Boolean);
                const ids: string[] = [];
                for (const part of parts) {
                  const partNorm = part.toLowerCase().trim();
                  // Match robusto: prima codice, poi descrizione, entrambi case-insensitive.
                  // Evitiamo includes bidirezionale che può creare collisioni/duplicati.
                  let energyUse = energyUses.find(
                    eu => (eu.code || '').toLowerCase().trim() === partNorm
                  );
                  if (!energyUse) {
                    energyUse = energyUses.find(
                      eu => (eu.description || '').toLowerCase().trim() === partNorm
                    );
                  }
                  if (energyUse?.id) ids.push(energyUse.id);
                }
                const dedupedIds = Array.from(new Set(ids));
                if (dedupedIds.length) {
                  itemData.energy_uses = dedupedIds;
                  itemData.all_energy_use = false;
                }
              }
            } else if (fieldName === 'all_energy_use') {
              if (
                value.toLowerCase() === 'true' ||
                value === '1' ||
                value.toLowerCase() === 'sì' ||
                value.toLowerCase() === 'si'
              ) {
                itemData.all_energy_use = true;
                itemData.energy_uses = [];
              }
            }
            // Gestisci il risparmio energetico annuo per vettore (campo manuale)
            else if (fieldName.startsWith('annual_energy_saving_')) {
              const vectorId = fieldName.replace('annual_energy_saving_', '');
              if (!vectorId) return;
              if (!itemData.annual_energy_saving_by_vector) {
                itemData.annual_energy_saving_by_vector = {};
              }
              if (!itemData.to_evaluated_by_vector) {
                itemData.to_evaluated_by_vector = {};
              }
              const normalized = value.toLowerCase().trim();
              if (normalized === 'da valutare') {
                itemData.to_evaluated_by_vector[vectorId] = true;
                itemData.annual_energy_saving_by_vector[vectorId] = null;
              } else {
                const num = parseNumber(value);
                if (num !== undefined) {
                  itemData.to_evaluated_by_vector[vectorId] = false;
                  itemData.annual_energy_saving_by_vector[vectorId] = num;
                }
              }
            }
            // Campi consumption_before_* e cost_before sono solo in griglia (calcolati), non inviati in create
            else if (
              fieldName === 'consumption_import_single' ||
              fieldName.startsWith('consumption_before_') ||
              fieldName === 'cost_before' ||
              fieldName === 'annual_co2_saving'
            ) {
              // skip (campi calcolati)
            }
            // Import manuale: questi campi non devono essere ricalcolati dopo import
            else if (
              fieldName === 'expected_economic_saving' ||
              fieldName === 'payback_time' ||
              fieldName === 'van_5_10_years' ||
              fieldName === 'tir_10_years'
            ) {
              const num = parseNumber(value);
              if (num !== undefined) {
                itemData[fieldName] = num;
                if (fieldName === 'expected_economic_saving') {
                  itemData.expected_economic_saving_manual = true;
                } else if (fieldName === 'payback_time') {
                  itemData.payback_time_manual = true;
                } else if (fieldName === 'van_5_10_years') {
                  itemData.van_5_10_years_manual = true;
                } else if (fieldName === 'tir_10_years') {
                  itemData.tir_10_years_manual = true;
                }
              }
            }
            // Gestisci i campi numerici
            else if (
              fieldName === 'cost_before_ea' ||
              fieldName === 'investment' ||
              fieldName === 'useful_life'
            ) {
              const num = parseNumber(value);
              if (num !== undefined) itemData[fieldName] = num;
            }
            // Tutti gli altri campi come stringa
            else {
              itemData[fieldName] = value;
            }
          });

          newItems.push(itemData as DiagnosisInterventionsCreateDTO);
        }

        if (newItems.length === 0) {
          toast.error('Nessun dato valido trovato');
          return;
        }

        // Salva tutti gli item
        try {
          for (const item of newItems) {
            await apiClient.create_item_diagnosis_interventions__post(item as any);
          }

          // Chiudi il dialog
          setPasteMappingDialog({ open: false, clipboardData: [] });

          toast.success(`${newItems.length} intervento/i creato/i con successo`);
          queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
        } catch (error: any) {
          queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
          toast.error(error?.message || 'Errore durante la creazione degli interventi');
        }
      },
      [
        diagnosisId,
        pasteMappingDialog.clipboardData,
        energyVectors,
        energyUses,
        queryClient,
        vectorTypeOptions,
      ]
    );

    /** POST intervento sulla diagnosi corrente (body già rimappato da `buildInterventionCloneCreateDto`). */
    const importInterventionMutation = useMutation(
      async (payload: IDiagnosisInterventionsCreateDTO) =>
        apiClient.create_item_diagnosis_interventions__post(payload as any),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
          toast.success('Intervento aggiunto agli interventi attuali');
        },
        onError: (e: any) => {
          toast.error(e?.message || 'Impossibile aggiungere l’intervento');
        },
      }
    );

    /**
     * Copia un intervento dalla diagnosi `sourceDiagnosisId` alla corrente: allinea id vettore/uso tra
     * diagnosi, costruisce il body POST e rifiuta se il codice intervento è già presente negli attuali.
     */
    const handleImportInterventionToCurrent = useCallback(
      async (
        sourceRow: DiagnosisInterventionsReadDTO | InterventionGridRow,
        sourceDiagnosisId: string
      ) => {
        const intervention =
          (sourceRow as InterventionGridRow)._raw ?? (sourceRow as DiagnosisInterventionsReadDTO);
        if (!diagnosisId) return;
        const codeNorm = (intervention.code || '').trim();
        if (codeNorm) {
          const exists = interventions.some(i => (i.code || '').trim() === codeNorm);
          if (exists) {
            toast.error(`Esiste già un intervento attivo con ID "${codeNorm}".`);
            return;
          }
        }
        let srcVec: DiagnosisEnergyVectorsReadDTO[] =
          sourceDiagnosisId === previousDiagnosisId
            ? previousVectors
            : (aiVectorsByDx[sourceDiagnosisId] ?? []);
        let srcUses: DiagnosisEnergyUseReadDTO[] =
          sourceDiagnosisId === previousDiagnosisId
            ? previousUses
            : (aiUsesByDx[sourceDiagnosisId] ?? []);
        if (!srcVec.length) {
          try {
            srcVec = await queryClient.fetchQuery(
              [`diagnosis-energy-vectors-${sourceDiagnosisId}`],
              () =>
                apiClient.search_by_diagnosis_id_diagnosis_energy_vectors_search_by_diagnosis_id__diagnosis_id__get(
                  sourceDiagnosisId
                )
            );
          } catch {
            srcVec = [];
          }
        }
        if (!srcUses.length) {
          try {
            srcUses = await queryClient.fetchQuery(
              [`diagnosis-energy-use-${sourceDiagnosisId}`],
              () =>
                apiClient.search_by_diagnosis_id_diagnosis_energy_use_search_by_diagnosis_id__diagnosis_id__get(
                  sourceDiagnosisId
                )
            );
          } catch {
            srcUses = [];
          }
        }
        const vMap = buildVectorIdMap(srcVec, energyVectors);
        const uMap = buildEnergyUseIdMap(srcUses, energyUses);
        const payload = buildInterventionCloneCreateDto(intervention, diagnosisId, vMap, uMap);
        if (!payload) {
          toast.error(
            'Impossibile mappare vettori o usi energetici verso la diagnosi attuale. Verifica che le configurazioni coincidano.'
          );
          return;
        }
        importInterventionMutation.mutate(payload);
      },
      [
        aiUsesByDx,
        aiVectorsByDx,
        diagnosisId,
        energyUses,
        energyVectors,
        importInterventionMutation,
        interventions,
        previousDiagnosisId,
        previousUses,
        previousVectors,
        queryClient,
      ]
    );

    const handleImportAiIntervention = useCallback(
      async (item: AiSearchResultItem, row: DiagnosisInterventionsReadDTO) => {
        if (!diagnosisId) return;
        const codeNorm = (row.code || '').trim();
        if (codeNorm) {
          const exists = interventions.some(i => (i.code || '').trim() === codeNorm);
          if (exists) {
            toast.error(`Esiste già un intervento attivo con ID "${codeNorm}".`);
            return;
          }
        }
        try {
          if (item.tipo === 'external') {
            await importAiItemToDiagnosis({
              diagnosisId,
              item,
              apiClient,
              queryClient,
              existingCodes: interventions
                .map(i => (i.code || '').trim())
                .filter(Boolean) as string[],
            });
          } else if (row.diagnosis_id && row.diagnosis_id !== diagnosisId) {
            await handleImportInterventionToCurrent(
              (row as InterventionGridRow)._raw ?? row,
              row.diagnosis_id
            );
          } else {
            await importAiItemToDiagnosis({
              diagnosisId,
              item,
              apiClient,
              queryClient,
              existingCodes: interventions
                .map(i => (i.code || '').trim())
                .filter(Boolean) as string[],
            });
          }
          queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
          //toast.success('Intervento aggiunto agli interventi attuali');
        } catch (e: unknown) {
          toast.error(e instanceof Error ? e.message : 'Impossibile aggiungere l’intervento');
        }
      },
      [diagnosisId, handleImportInterventionToCurrent, interventions, queryClient]
    );

    /** Righe griglia: colonne consumo/risparmio sono sempre ancorate ai vettori della diagnosi *attuale*. */
    const currentGridRows = useMemo(
      () => buildInterventionGridRows(interventions, energyVectors, null, null),
      [interventions, energyVectors]
    );

    /** Per la diagnosi precedente si rimappano consumi/risparmi dalle chiavi vettore «vecchie» alle attuali. */
    const previousGridRows = useMemo(() => {
      if (!previousDiagnosisId) return [];
      const vMap = buildVectorIdMap(previousVectors, energyVectors);
      const uMap = buildEnergyUseIdMap(previousUses, energyUses);
      return buildInterventionGridRows(previousInterventions, energyVectors, vMap, uMap);
    }, [
      previousDiagnosisId,
      previousInterventions,
      previousVectors,
      energyVectors,
      previousUses,
      energyUses,
    ]);

    /** Griglia AI: ogni riga può provenire da diagnosi diverse → remap vettori/uso per diagnosi sorgente. */
    const aiGridRows = useMemo(() => {
      return aiInterventions.map(row => {
        const sid = row.diagnosis_id;
        const item = row.id ? aiItemByRowId.get(row.id) : undefined;
        const fonte = item ? aiFonteFromItem(item) : '';
        const base = !sid
          ? buildInterventionGridRows([row], energyVectors, null, null)[0]
          : (() => {
              const srcVec = aiVectorsByDx[sid] ?? [];
              const srcUses = aiUsesByDx[sid] ?? [];
              const vMap = buildVectorIdMap(srcVec, energyVectors);
              const uMap = buildEnergyUseIdMap(srcUses, energyUses);
              return buildInterventionGridRows([row], energyVectors, vMap, uMap)[0];
            })();
        return { ...base, fonte };
      });
    }, [aiInterventions, aiItemByRowId, aiVectorsByDx, aiUsesByDx, energyVectors, energyUses]);

    /** Testo vettori per le tabelle riepilogo (lookup sui vettori della diagnosi di riferimento). */
    const formatVectorsLabel = useCallback(
      (inv: DiagnosisInterventionsReadDTO, lookupVectors: DiagnosisEnergyVectorsReadDTO[]) => {
        const ids = inv.energy_vectors ?? [];
        if (!ids.length) return '-';
        return (
          ids
            .map(id => {
              const vector = lookupVectors.find(v => v.id === id);
              return vector ? getEnergyVectorOptionLabel(vector, vectorTypeOptions, false) : id;
            })
            .filter(Boolean)
            .join(', ') || '-'
        );
      },
      [vectorTypeOptions]
    );

    /** Riepilogo «Attuali»: niente import (sono già nella diagnosi corrente). */
    const summaryRowsCurrent = useMemo(
      () =>
        interventions.map(i => ({
          summaryRowId: `cur-${i.id}`,
          code: i.code || i.id || '-',
          title: i.title || '-',
          vectorsLabel: formatVectorsLabel(i, energyVectors),
        })),
      [formatVectorsLabel, interventions, energyVectors]
    );

    /** `_raw` / `_srcDx` servono al pulsante ← nel riepilogo per lo stesso flusso di import della griglia. */
    const summaryRowsPrevious = useMemo(
      () =>
        previousInterventions.map(i => ({
          summaryRowId: `prev-${i.id}`,
          code: i.code || i.id || '-',
          title: i.title || '-',
          vectorsLabel: formatVectorsLabel(i, previousVectors),
          _srcDx: previousDiagnosisId,
          _raw: i,
        })),
      [formatVectorsLabel, previousDiagnosisId, previousInterventions, previousVectors]
    );

    /** Riepilogo AI: stessi metadati di `summaryRowsPrevious` per abilitare l’import dal riepilogo. */
    const summaryRowsAi = useMemo(
      () =>
        aiInterventionBundles.map(({ item, row: i }) => {
          const sid = i.diagnosis_id || '';
          const lookup = sid ? (aiVectorsByDx[sid] ?? []) : [];
          return {
            summaryRowId: `ai-${i.id}`,
            code: i.code || i.id || '-',
            title: i.title || '-',
            vectorsLabel: formatVectorsLabel(i, lookup),
            fonte: aiFonteFromItem(item),
            _srcDx: sid || undefined,
            _raw: i,
            _aiItem: item,
          };
        }),
      [aiInterventionBundles, aiVectorsByDx, formatVectorsLabel]
    );

    /** Colonna extra nelle griglie «precedente» / «AI»: copia riga negli interventi attuali. */
    const importActionField: ReadOnlyField = {
      field: '_import',
      headerName: '',
      width: 56,
      sticky: 'left',
      renderCell: (params: any) => {
        const row = params?.row as DiagnosisInterventionsReadDTO;
        if (mainView === 'ai') {
          const item = row?.id ? aiItemByRowId.get(row.id) : undefined;
          if (!item) return null;
          return (
            <Tooltip title="Aggiungi agli interventi attuali">
              <IconButton
                size="small"
                disabled={importInterventionMutation.isLoading}
                onClick={() => void handleImportAiIntervention(item, row)}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }
        const sourceDx = mainView === 'previous' ? previousDiagnosisId : row?.diagnosis_id;
        if (!sourceDx) return null;
        return (
          <Tooltip title="Aggiungi agli interventi attuali">
            <IconButton
              size="small"
              disabled={importInterventionMutation.isLoading}
              onClick={() => void handleImportInterventionToCurrent(row, sourceDx)}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    };

    /** Stessa azione «←» del dettaglio, ma sulle righe compatte del riepilogo (`_raw`, `_srcDx`). */
    const summaryImportField: ReadOnlyField = {
      field: '_import',
      headerName: '',
      width: 52,
      renderCell: (params: any) => {
        const row = params?.row as {
          _srcDx?: string;
          _raw?: DiagnosisInterventionsReadDTO;
          _aiItem?: AiSearchResultItem;
        };
        if (!row._raw) return null;
        return (
          <Tooltip title="Aggiungi agli interventi attuali">
            <IconButton
              size="small"
              disabled={importInterventionMutation.isLoading}
              onClick={() => {
                if (row._aiItem) {
                  void handleImportAiIntervention(row._aiItem, row._raw!);
                } else if (row._srcDx) {
                  void handleImportInterventionToCurrent(row._raw!, row._srcDx);
                }
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    };

    const summaryFieldsBase: ReadOnlyField[] = [
      { field: 'code', headerName: 'ID intervento', width: 160 },
      { field: 'title', headerName: 'Titolo', width: 220 },
      { field: 'vectorsLabel', headerName: 'Vettori energetici', width: 280 },
    ];

    const summaryFieldsWithImport: ReadOnlyField[] = [summaryImportField, ...summaryFieldsBase];
    const summaryFieldsAi: ReadOnlyField[] = [
      summaryImportField,
      ...summaryFieldsBase,
      aiFonteReadOnlyField,
    ];

    /** Griglia dettaglio: colonna import solo dove ha senso (non sugli «Attuali»). */
    const detailGridFields =
      mainView === 'previous'
        ? [importActionField, ...fields]
        : mainView === 'ai'
          ? [importActionField, aiFonteReadOnlyField, ...fields]
          : fields;
    const detailGridRows =
      mainView === 'current'
        ? currentGridRows
        : mainView === 'previous'
          ? previousGridRows
          : aiGridRows;
    const detailLoading =
      mainView === 'current'
        ? isLoading
        : mainView === 'previous'
          ? isLoadingPrevious || isLoadingPreviousDiagnosis
          : isLoadingAiInterventions;

    const actions: RowAction[] = [
      {
        label: 'Sposta su',
        icon: <ArrowUpwardIcon />,
        onClick: (_id, row) => moveUpMutation.mutate(String(row?.id ?? _id)),
        color: 'inherit',
        visible: row => {
          const idx = interventions.findIndex(i => (i.id ?? '') === (row?.id ?? ''));
          return idx > 0;
        },
      },
      {
        label: 'Sposta giù',
        icon: <ArrowDownwardIcon />,
        onClick: (_id, row) => moveDownMutation.mutate(String(row?.id ?? _id)),
        color: 'inherit',
        visible: row => {
          const idx = interventions.findIndex(i => (i.id ?? '') === (row?.id ?? ''));
          return idx >= 0 && idx < interventions.length - 1;
        },
      },
      {
        label: 'Modifica',
        icon: <EditIcon />,
        onClick: handleEditClick,
        color: 'inherit',
      },
      {
        label: 'Elimina',
        icon: <DeleteIcon />,
        onClick: handleDeleteClick,
        color: 'inherit',
      },
    ];

    if (!diagnosisId) {
      return (
        <Typography color="text.secondary">
          Salva la diagnosi per gestire gli interventi di efficientamento.
        </Typography>
      );
    }

    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={3}>
            <Typography variant="h6">Interventi</Typography>
            {/* Tab «card»: stesse colonne della griglia principale; contenuto da fonte diversa. */}
            <ToggleButtonGroup
              value={mainView}
              exclusive
              onChange={(_e, v) => v != null && setMainView(v)}
              size="small"
              color="primary"
            >
              <ToggleButton value="current" sx={{ px: 2 }}>
                Attuali
              </ToggleButton>
              <ToggleButton
                value="previous"
                /* Disabilita solo dopo risposta: nessun id se API ha restituito null (prima diagnosi sul sito base). */
                disabled={!previousDiagnosisId && !isLoadingPreviousDiagnosis}
                sx={{ px: 2 }}
              >
                Diagnosi precedente
              </ToggleButton>
              <ToggleButton value="ai" sx={{ px: 2 }}>
                Suggeriti AI
              </ToggleButton>
              <ToggleButton value="summary" sx={{ px: 2 }}>
                Riepilogo
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <IconButton
              color="primary"
              aria-label="Ricerca AI interventi"
              onClick={() => setAiSearchOpen(true)}
              size="medium"
            >
              <SearchIcon />
            </IconButton>
            <Button
              onClick={() => setUnitCostOverrideDialogOpen(true)}
              variant="contained"
              startIcon={<EuroIcon />}
            >
              Override costi unitari ({unitCostOverrides.length})
            </Button>
            <Button onClick={handlePaste} variant="contained" startIcon={<ContentPasteIcon />}>
              Incolla righe
            </Button>
          </Stack>
        </Stack>

        {mainView === 'summary' ? (
          /* Tre colonne affiancate: stesso tipo di informazione per attuali / precedente / AI. */
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems="stretch">
              <Paper variant="outlined" sx={{ flex: 1, p: 1.5, minWidth: 0, borderRadius: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Attuali
                </Typography>
                <ReadOnlyDataGrid
                  fields={summaryFieldsBase}
                  data={summaryRowsCurrent}
                  actions={[]}
                  height="calc(100vh - 430px)"
                  getId={row => (row as { summaryRowId: string }).summaryRowId}
                  loading={isLoading}
                />
              </Paper>
              <Paper variant="outlined" sx={{ flex: 1, p: 1.5, minWidth: 0, borderRadius: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Diagnosi precedente
                </Typography>
                {!previousDiagnosisId ? (
                  <Typography variant="body2" color="text.secondary">
                    Nessuna diagnosi precedente sullo stesso sito base.
                  </Typography>
                ) : (
                  <ReadOnlyDataGrid
                    fields={summaryFieldsWithImport}
                    data={summaryRowsPrevious}
                    actions={[]}
                    height="calc(100vh - 430px)"
                    getId={row => (row as { summaryRowId: string }).summaryRowId}
                    loading={isLoadingPrevious || isLoadingPreviousDiagnosis}
                  />
                )}
              </Paper>
              <Paper variant="outlined" sx={{ flex: 1, p: 1.5, minWidth: 0, borderRadius: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Suggeriti AI
                </Typography>
                {aiInterventions.length === 0 && !isLoadingAiInterventions ? (
                  <Typography variant="body2" color="text.secondary">
                    Nessun intervento suggerito dall&apos;AI per questa diagnosi.
                  </Typography>
                ) : (
                  <ReadOnlyDataGrid
                    fields={summaryFieldsAi}
                    data={summaryRowsAi}
                    actions={[]}
                    height="calc(100vh - 430px)"
                    getId={row => (row as { summaryRowId: string }).summaryRowId}
                    loading={isLoadingAiInterventions}
                  />
                )}
              </Paper>
            </Stack>
          </Stack>
        ) : (
          <Box>
            {mainView === 'previous' && !previousDiagnosisId && !isLoadingPreviousDiagnosis && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Nessuna diagnosi precedente sullo stesso sito base per questa diagnosi.
              </Typography>
            )}
            {mainView === 'ai' && aiInterventions.length === 0 && !isLoadingAiInterventions && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Nessun intervento suggerito dall&apos;AI per questa diagnosi.
              </Typography>
            )}
            {!(
              (mainView === 'previous' && !previousDiagnosisId && !isLoadingPreviousDiagnosis) ||
              (mainView === 'ai' && aiInterventions.length === 0 && !isLoadingAiInterventions)
            ) && (
              <ReadOnlyDataGrid
                fields={detailGridFields}
                data={detailGridRows}
                actions={mainView === 'current' ? actions : []}
                height="calc(100vh - 380px)"
                getId={row => row.id ?? ''}
                loading={detailLoading}
              />
            )}
          </Box>
        )}
        <EditInterventionDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          intervention={selectedIntervention}
          diagnosisId={diagnosisId}
        />
        <UnitCostOverrideDialog
          open={unitCostOverrideDialogOpen}
          onClose={() => setUnitCostOverrideDialogOpen(false)}
          diagnosisId={diagnosisId}
        />
        <ConfirmDialog
          open={deleteDialogState.open}
          onCancel={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          title="Elimina intervento"
          content="Sei sicuro di voler eliminare questo intervento?"
        />
        {/* Dialog per mapping colonne clipboard */}
        <ColumnMappingDialog
          open={pasteMappingDialog.open}
          onClose={() => setPasteMappingDialog({ open: false, clipboardData: [] })}
          onImport={handleImportWithMapping}
          clipboardData={pasteMappingDialog.clipboardData}
          fields={pasteMappingFields.map(f => ({
            field: f.field,
            headerName: f.headerName || f.field,
            width: f.width,
            calculated:
              f.calculated === true ||
              (typeof f.field === 'string' &&
                (f.field.startsWith('consumption_before_') ||
                  f.field === 'cost_before' ||
                  f.field === 'annual_co2_saving')),
          }))}
          dataOrientation="column"
        />
        <AiSearchModal
          open={aiSearchOpen}
          onClose={() => setAiSearchOpen(false)}
          fixedEntityType="intervention"
          diagnosisId={diagnosisId}
          onInsert={async item => {
            if (!diagnosisId) return;
            const row = await resolveAiInterventionRow(item, apiClient);
            await handleImportAiIntervention(item, row);
          }}
        />
        <ActionSpacer></ActionSpacer>
      </Stack>
    );
  })
);

DiagnosisInterventionsStep.displayName = 'DiagnosisInterventionsStep';
